import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useId } from "react";

import {
  ITEM_STATUS_LABELS,
  type BackgroundItemStatus,
} from "@/api/backgroundVocabulary";
import type {
  BackgroundItemView,
  BackgroundRuleView,
  EffectiveSafetyNet,
} from "@/lib/backgroundDomains";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";

import {
  ItemReferencePicker,
  KeywordListField,
  type ReferenceOption,
} from "./BackgroundReferences";

/**
 * A domain's deterministic rules and safety nets, as named controls (#913).
 *
 * ## WHAT THESE TWO GROUPS ARE, AND WHY THEY ARE NOT ONE LIST
 *
 * `conditional_trigger_rules` is ORDERED and runs on every turn: when the gate
 * prompt holds a status, the rule activates some prompts and skips others. It is
 * the half of "conditional" that is deterministic — the item's own `trigger`
 * note is prose the extraction model reads, and it decides nothing.
 *
 * `safety_net_triggers` is a NAMED MAP and is the completeness backstop: when a
 * gate prompt's captured value matches (or, for the source's `_military_bulk`,
 * fails to match) a keyword, the named targets are activated even though no
 * ordered rule covered the case. It is BG completeness logic and never the
 * runtime self-harm detector, which is tenant-owned and lives nowhere near this
 * asset (S7 contract §4).
 *
 * Two different shapes, two different lifetimes and two different failure modes,
 * so two named regions rather than one editor over both.
 *
 * ## THE TWO COLLECTIONS ARE OWNED DIFFERENTLY, AND NEITHER IS "THE DOCUMENT ON
 * SCREEN"
 *
 * `conditional_trigger_rules` is ANCHOR-OWNED. `_merge_lang_configs` builds a
 * domain from `dict(he_cfg)` and never consults another language's rule list, so
 * a rule written anywhere else is one the runtime cannot run.
 *
 * `safety_net_triggers` is a MERGED SET. The engine unions the trigger NAMES
 * across languages and uses the other language's entry for a name the active one
 * does not carry, so what this panel receives is `effectiveSafetyNets` — what
 * RUNS — and each row carries the document its entry actually lives in. An edit
 * addresses that document, so editing an inherited net edits the copy the
 * runtime is reading; adding writes into the anchor, so a new name is primary;
 * and removing strips the name from every language, because removing one copy
 * reveals the other rather than deleting the net.
 *
 * This comment used to say both collections were written into the language being
 * edited. Both halves of that were wrong, and each produced a defect: rules
 * written to a document nothing reads, and a panel that hid nets which were
 * firing. `lib/backgroundDomains` documents the ownership in full.
 */

export interface BackgroundDomainRulesProps {
  /** The ANCHOR's prompts — the ids the runtime resolves a reference against. */
  items: readonly BackgroundItemView[];
  rules: readonly BackgroundRuleView[] | null;
  /**
   * The nets the RUNTIME reads for the anchor language — the union across
   * stored languages the engine builds, not one document's table. See
   * `effectiveSafetyNets`.
   */
  safetyNets: readonly EffectiveSafetyNet[];
  /** The tracker statuses the service admits, in its own order. */
  statuses: readonly BackgroundItemStatus[];
  /**
   * Whether the service has ANSWERED, as its own fact rather than inferred from
   * `statuses.length`.
   *
   * An empty list means three different things — the read is in flight, the read
   * failed, or this engine really publishes nothing — and only the third of them
   * licenses calling a stored status invalid. Inferring it from the array is how
   * `StatusPicker` came to render every stored status red while the request was
   * still open.
   */
  vocabularyResolved: boolean;
  /** Why the vocabulary is not available, or `null`. Never an unexplained empty select. */
  vocabularyRefusal: string | null;
  /** The ANCHOR's language: the rules and their keyword lists live there. */
  language: string | null;
  /**
   * The language `safetyNets` was merged FOR — the one whose table wins per name
   * before the engine falls back to another.
   *
   * Not "the table on screen": there is no single table on screen any more. Each
   * row names the document its own entry lives in, and this is the reference
   * point those rows are described against.
   */
  safetyNetLanguage: string | null;
  readOnly: boolean;
  onAddRule: () => void;
  onRemoveRule: (index: number) => void;
  onMoveRule: (index: number, to: number) => void;
  onChangeRuleGate: (
    index: number,
    field: "when" | "equals",
    value: string,
  ) => void;
  onChangeRuleList: (
    index: number,
    field:
      | "value_contains"
      | "value_not_contains"
      | "trigger_items"
      | "deny_items",
    values: string[],
  ) => void;
  onAddSafetyNet: () => void;
  onRemoveSafetyNet: (name: string) => void;
  /** Addressed by the DOCUMENT the entry lives in: an inherited net is edited
   * where the runtime is reading it, never copied into another language. */
  onChangeSafetyNetGateItem: (
    documentId: string,
    name: string,
    value: string,
  ) => void;
  onChangeSafetyNetList: (
    documentId: string,
    name: string,
    field: "gate_statuses" | "keywords" | "exclude_keywords" | "target_items",
    values: string[],
  ) => void;
}

export function BackgroundDomainRules(props: BackgroundDomainRulesProps) {
  const {
    items,
    rules,
    safetyNets,
    statuses,
    vocabularyResolved,
    vocabularyRefusal,
    language,
    safetyNetLanguage,
    readOnly,
    onAddRule,
    onAddSafetyNet,
  } = props;
  const options: ReferenceOption[] = items.map((item) => ({
    id: item.id,
    description: item.description,
  }));

  return (
    <div className="space-y-6">
      {vocabularyRefusal ? (
        // NOT a disabled select with six invented options behind it. The
        // statuses are the pinned engine's, read from the service; if that read
        // failed, the honest surface says so and keeps every stored value
        // visible rather than offering an operator it cannot vouch for.
        <Notice role="alert">{vocabularyRefusal}</Notice>
      ) : null}

      <section className="space-y-3">
        <header className="space-y-1">
          <h3 className="text-sm font-medium text-foreground">
            Conditional trigger rules
          </h3>
          <p className="text-xs text-muted-foreground">
            Checked in order after every answer. When the gate prompt holds the
            status named, the rule activates the prompts it triggers and skips
            the ones it denies.
          </p>
        </header>

        {rules === null || rules.length === 0 ? (
          <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            No conditional rules. Every prompt in this domain is asked in order,
            and a conditional prompt has nothing to activate it.
          </p>
        ) : (
          <ol aria-label="Conditional trigger rules, in order" className="space-y-3">
            {rules.map((rule, index) => (
              <RuleRow
                key={index}
                rule={rule}
                index={index}
                total={rules.length}
                options={options}
                statuses={statuses}
                vocabularyResolved={vocabularyResolved}
                language={language}
                readOnly={readOnly}
                onRemove={() => props.onRemoveRule(index)}
                onMove={(to) => props.onMoveRule(index, to)}
                onChangeGate={(field, value) =>
                  props.onChangeRuleGate(index, field, value)
                }
                onChangeList={(field, values) =>
                  props.onChangeRuleList(index, field, values)
                }
              />
            ))}
          </ol>
        )}

        {readOnly ? null : (
          <Button
            variant="outline"
            size="sm"
            disabled={items.length === 0}
            onClick={onAddRule}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            Add rule
          </Button>
        )}
      </section>

      <section className="space-y-3">
        <header className="space-y-1">
          <h3 className="text-sm font-medium text-foreground">
            Safety-net triggers
          </h3>
          <p className="text-xs text-muted-foreground">
            A completeness backstop: when the gate prompt&apos;s answer matches
            the keywords — or, with exclusions, fails to match them — the named
            prompts are activated even though no ordered rule covered the case.
            This is interview completeness, not the runtime safety detector.
          </p>
        </header>

        {safetyNets.length === 0 ? (
          <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            No safety nets. Prompts are activated by the ordered rules alone.
          </p>
        ) : (
          <ul aria-label="Safety-net triggers" className="space-y-3">
            {safetyNets.map((entry) => (
              <SafetyNetRow
                key={entry.net.name}
                entry={entry}
                options={options}
                statuses={statuses}
                vocabularyResolved={vocabularyResolved}
                anchorLanguage={safetyNetLanguage}
                readOnly={readOnly}
                onRemove={() => props.onRemoveSafetyNet(entry.net.name)}
                onChangeGateItem={(value) =>
                  props.onChangeSafetyNetGateItem(
                    entry.documentId,
                    entry.net.name,
                    value,
                  )
                }
                onChangeList={(field, values) =>
                  props.onChangeSafetyNetList(
                    entry.documentId,
                    entry.net.name,
                    field,
                    values,
                  )
                }
              />
            ))}
          </ul>
        )}

        {readOnly ? null : (
          <Button
            variant="outline"
            size="sm"
            disabled={items.length === 0}
            onClick={onAddSafetyNet}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            Add safety net
          </Button>
        )}
      </section>
    </div>
  );
}

function RuleRow({
  rule,
  index,
  total,
  options,
  statuses,
  vocabularyResolved,
  language,
  readOnly,
  onRemove,
  onMove,
  onChangeGate,
  onChangeList,
}: {
  rule: BackgroundRuleView;
  index: number;
  total: number;
  options: readonly ReferenceOption[];
  statuses: readonly BackgroundItemStatus[];
  vocabularyResolved: boolean;
  language: string | null;
  readOnly: boolean;
  onRemove: () => void;
  onMove: (to: number) => void;
  onChangeGate: (field: "when" | "equals", value: string) => void;
  onChangeList: (
    field:
      | "value_contains"
      | "value_not_contains"
      | "trigger_items"
      | "deny_items",
    values: string[],
  ) => void;
}) {
  const position = index + 1;
  const inert =
    (rule.triggerItems ?? []).length === 0 && (rule.denyItems ?? []).length === 0;

  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-foreground">Rule {position}</h4>
        {readOnly ? null : (
          <div className="flex items-center gap-0.5">
            {/* THE POSITION IS IN THE NAME here, unlike the prompt rows, and
                for the opposite reason: a rule has no stable id of its own — it
                is identified by where it sits in the ordered list — so the
                position is the only thing a clinician can be told to look at. */}
            <IconButton
              label={`Move rule ${position} up`}
              icon={<ArrowUp className="h-4 w-4" />}
              disabled={index === 0}
              onClick={() => onMove(index - 1)}
            />
            <IconButton
              label={`Move rule ${position} down`}
              icon={<ArrowDown className="h-4 w-4" />}
              disabled={index === total - 1}
              onClick={() => onMove(index + 1)}
            />
            <IconButton
              label={`Remove rule ${position}`}
              icon={<Trash2 className="h-4 w-4" />}
              onClick={onRemove}
            />
          </div>
        )}
      </div>

      <div className="mt-2 grid gap-3 @2xl:grid-cols-2">
        <ReferenceSelect
          label={`Rule ${position} gate prompt`}
          description="The prompt whose answer this rule reads."
          value={rule.when}
          options={options}
          readOnly={readOnly}
          onChange={(value) => onChangeGate("when", value)}
        />
        <StatusSelect
          label={`Rule ${position} gate status`}
          description="The status the gate prompt has to hold for this rule to run."
          value={rule.equals}
          statuses={statuses}
          vocabularyResolved={vocabularyResolved}
          readOnly={readOnly}
          onChange={(value) => onChangeGate("equals", value)}
        />
      </div>

      <div className="mt-3 grid gap-3 @2xl:grid-cols-2">
        <KeywordListField
          label={`Rule ${position} — only when the answer contains`}
          description="One phrase per line. The rule runs only when the captured answer contains one of them. Leave empty for no filter."
          values={rule.valueContains}
          language={language}
          readOnly={readOnly}
          onChange={(values) => onChangeList("value_contains", values)}
        />
        <KeywordListField
          label={`Rule ${position} — except when the answer contains`}
          description="One phrase per line. The rule is skipped when the captured answer contains one of them."
          values={rule.valueNotContains}
          language={language}
          readOnly={readOnly}
          onChange={(values) => onChangeList("value_not_contains", values)}
        />
      </div>

      <div className="mt-3 grid gap-3 @2xl:grid-cols-2">
        <ItemReferencePicker
          legend={`Rule ${position} activates`}
          description="Prompts this rule asks for."
          options={options}
          selected={rule.triggerItems ?? []}
          readOnly={readOnly}
          emptyLabel="This domain has no prompts to activate."
          onChange={(values) => onChangeList("trigger_items", values)}
        />
        <ItemReferencePicker
          legend={`Rule ${position} skips`}
          description="Prompts this rule marks as not applicable."
          options={options}
          selected={rule.denyItems ?? []}
          readOnly={readOnly}
          emptyLabel="This domain has no prompts to skip."
          onChange={(values) => onChangeList("deny_items", values)}
        />
      </div>

      {inert ? (
        // ADVISORY, NOT A REFUSAL. The service admits a rule with neither list —
        // both are optional — so a client that blocked the save would refuse a
        // document the contract accepts. What it would do at runtime is
        // nothing, and saying so is the whole of this sentence.
        <p className="mt-3 text-xs text-muted-foreground">
          This rule activates and skips nothing, so it has no effect at runtime.
        </p>
      ) : null}
    </li>
  );
}

function SafetyNetRow({
  entry,
  options,
  statuses,
  vocabularyResolved,
  anchorLanguage,
  readOnly,
  onRemove,
  onChangeGateItem,
  onChangeList,
}: {
  entry: EffectiveSafetyNet;
  options: readonly ReferenceOption[];
  statuses: readonly BackgroundItemStatus[];
  vocabularyResolved: boolean;
  anchorLanguage: string | null;
  readOnly: boolean;
  onRemove: () => void;
  onChangeGateItem: (value: string) => void;
  onChangeList: (
    field: "gate_statuses" | "keywords" | "exclude_keywords" | "target_items",
    values: string[],
  ) => void;
}) {
  const { net, inherited, alsoIn, language } = entry;
  const namesOwnTarget = !net.name.startsWith("_");
  const included = (net.keywords ?? []).length > 0;
  const excluded = (net.excludeKeywords ?? []).length > 0;
  const where = language ?? "all languages";

  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-foreground">
          Safety net <code dir="ltr">{net.name}</code>
        </h4>
        {readOnly ? null : (
          <IconButton
            label={`Remove safety net ${net.name}`}
            icon={<Trash2 className="h-4 w-4" />}
            onClick={onRemove}
          />
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        The name is this net&apos;s runtime identity and is never renamed.
        Removing it removes the name from every stored language, because the
        runtime falls back to the other one.
      </p>
      {/* WHERE THIS ENTRY LIVES, said on the row. The engine unions the names
          across languages and uses the other language's entry for a name the
          active one does not carry, so "which document am I editing" is not a
          detail — it is the difference between changing what runs and changing
          nothing. */}
      {inherited ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Stored in this domain&apos;s{" "}
          <code dir="ltr">{where}</code> document, and read for{" "}
          <code dir="ltr">{anchorLanguage ?? "the runtime"}</code> because there
          is no net of this name there. Editing it changes that document.
        </p>
      ) : null}
      {alsoIn.length > 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          A net of this name is also stored in{" "}
          {alsoIn.map((held) => (
            <code key={held} dir="ltr" className="mr-1">
              {held}
            </code>
          ))}
          . A conversation in that language reads its copy, not this one.
        </p>
      ) : null}

      <div className="mt-2 grid gap-3 @2xl:grid-cols-2">
        <ReferenceSelect
          label={`Safety net ${net.name} gate prompt`}
          description="The prompt whose captured answer this net reads."
          value={net.gateItem}
          options={options}
          readOnly={readOnly}
          onChange={onChangeGateItem}
        />
        <StatusPicker
          legend={`Safety net ${net.name} gate statuses`}
          description="Which statuses of the gate prompt this net reads. Leave empty for the engine's own default, captured."
          statuses={statuses}
          vocabularyResolved={vocabularyResolved}
          selected={net.gateStatuses ?? []}
          readOnly={readOnly}
          onChange={(values) => onChangeList("gate_statuses", values)}
        />
      </div>

      {/* THE TWO KEYWORD LISTS ARE MUTUALLY EXCLUSIVE AT THE ENGINE, and the
          engine is the authority: `apply_safety_net_triggers` reads
          `if include_kw: … elif exclude_kw:`, so an exclusion list is NEVER
          evaluated while an inclusion list is present. Offering both as
          independent fields let a clinician write "fires on A, except B" and
          get "fires on A" — the exclusion silently ignored, which is the class
          of failure this whole surface exists to remove. Neither source
          document uses both: `_military_bulk` excludes only,
          `immigration_transitions` includes only. */}
      <div className="mt-3 grid gap-3 @2xl:grid-cols-2">
        <KeywordListField
          label={`Safety net ${net.name} — keywords`}
          description={
            excluded
              ? "One phrase per line. The net fires when the captured answer contains one of them. Adding any keyword here replaces the exclusions below, which the runtime then stops reading."
              : "One phrase per line. The net fires when the captured answer contains one of them."
          }
          values={net.keywords}
          language={language}
          readOnly={readOnly}
          onChange={(values) => onChangeList("keywords", values)}
        />
        <KeywordListField
          label={`Safety net ${net.name} — excluded keywords`}
          description={
            included
              ? "The runtime reads exclusions only when there are no keywords above. Clear the keywords to make these take effect."
              : "One phrase per line. The net is held back when the captured answer contains one of them."
          }
          values={net.excludeKeywords}
          language={language}
          // DISABLED RATHER THAN CLEARED. The stored phrases are the
          // clinician's and nothing here may destroy them; what this refuses is
          // EDITING a list the runtime is currently ignoring, and the sentence
          // above says which field to clear to get it back.
          readOnly={readOnly || included}
          onChange={(values) => onChangeList("exclude_keywords", values)}
        />
      </div>

      {included && excluded ? (
        <Notice role="alert" className="mt-3">
          This net lists both keywords and exclusions. The runtime reads the
          keywords and ignores the exclusions entirely; clear the keywords above
          to make the exclusions take effect.
        </Notice>
      ) : null}

      <div className="mt-3">
        <ItemReferencePicker
          legend={`Safety net ${net.name} targets`}
          description={
            namesOwnTarget
              ? `Prompts this net activates. With none chosen it activates the prompt its own name spells, ${net.name}.`
              : "Prompts this net activates. Its name begins with an underscore, so it names no prompt of its own and at least one target is required."
          }
          options={options}
          selected={net.targetItems ?? []}
          readOnly={readOnly}
          emptyLabel="This domain has no prompts to activate."
          onChange={(values) => onChangeList("target_items", values)}
        />
      </div>
    </li>
  );
}

/** One item reference, as a single-choice picker over this domain's prompts. */
function ReferenceSelect({
  label,
  description,
  value,
  options,
  readOnly,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  options: readonly ReferenceOption[];
  readOnly: boolean;
  onChange: (value: string) => void;
}) {
  const inputId = useId();
  const descriptionId = useId();
  const known = options.some((option) => option.id === value);
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <Select
        id={inputId}
        dir="ltr"
        aria-describedby={descriptionId}
        aria-invalid={known ? undefined : true}
        value={value}
        disabled={readOnly}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        {/* A REFERENCE THAT NO LONGER RESOLVES stays visible as its own option
            rather than snapping to the first prompt in the list. The document
            holds it, the service refuses it by name, and silently re-pointing a
            clinical rule at a different prompt is the one behaviour this
            control must never have. */}
        {known ? null : (
          <option value={value}>{value || "(no prompt chosen)"} — missing</option>
        )}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.id}
            {option.description ? ` — ${option.description}` : ""}
          </option>
        ))}
      </Select>
      <p id={descriptionId} className="text-xs text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

/** One tracker status, as a picker over the service's published vocabulary. */
function StatusSelect({
  label,
  description,
  value,
  statuses,
  vocabularyResolved,
  readOnly,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  statuses: readonly BackgroundItemStatus[];
  vocabularyResolved: boolean;
  readOnly: boolean;
  onChange: (value: string) => void;
}) {
  const inputId = useId();
  const descriptionId = useId();
  // NOTHING IS UNKNOWN UNTIL THE VOCABULARY HAS ARRIVED, and that is now the
  // SERVICE'S OWN ANSWER rather than a guess from the array's length: an empty
  // list also means the read is in flight or failed, and neither licenses
  // calling content the service accepted invalid.
  const known = !vocabularyResolved || statuses.some((status) => status === value);
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <Select
        id={inputId}
        aria-describedby={descriptionId}
        aria-invalid={known ? undefined : true}
        value={value}
        disabled={readOnly}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        {statuses.some((status) => status === value) ? null : (
          <option value={value}>
            {value || "(no status chosen)"}
            {vocabularyResolved ? " — not a tracker status" : ""}
          </option>
        )}
        {statuses.map((status) => (
          <option key={status} value={status}>
            {ITEM_STATUS_LABELS[status]}
          </option>
        ))}
      </Select>
      <p id={descriptionId} className="text-xs text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

/** Several tracker statuses, on `ItemReferencePicker`'s rules. */
function StatusPicker({
  legend,
  description,
  statuses,
  vocabularyResolved,
  selected,
  readOnly,
  onChange,
}: {
  legend: string;
  description: string;
  statuses: readonly BackgroundItemStatus[];
  vocabularyResolved: boolean;
  selected: readonly string[];
  readOnly: boolean;
  onChange: (next: string[]) => void;
}) {
  const chosen = new Set(selected);
  const describedBy = useId();
  // SELECTED, BUT NOT IN THE VOCABULARY THIS DEPLOYMENT PUBLISHES — a status
  // the engine has since dropped. `ItemReferencePicker` had exactly this defect
  // and exactly this fix: a picker that draws only what it recognises makes the
  // rest invisible, and rebuilding its value from the same list drops them on
  // the first unrelated tick.
  //
  // `missing` IS THE ROW SET, `unpublished` IS THE CLAIM, and they are separate
  // on purpose: while the read is in flight every stored status is absent from
  // an empty list and still has to be shown and kept — it just may not be called
  // wrong, which is the guard `StatusSelect` already had and this control did
  // not.
  const missing = selected.filter(
    (status) => !statuses.some((known) => known === status),
  );
  const rows: { value: string; unpublished?: boolean }[] = [
    ...statuses.map((status) => ({ value: status as string })),
    ...missing.map((status) => ({ value: status, unpublished: vocabularyResolved })),
  ];
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm font-medium text-foreground">{legend}</legend>
      <p id={describedBy} className="text-xs text-muted-foreground">
        {description}
      </p>
      <div
        aria-describedby={describedBy}
        className="space-y-1 rounded-md border border-border p-2"
      >
        {rows.map((row) => (
          <div key={row.value}>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-primary"
                checked={chosen.has(row.value)}
                disabled={readOnly}
                aria-invalid={row.unpublished || undefined}
                // APPEND ON TICK, REMOVE IN PLACE ON UNTICK —
                // `ItemReferencePicker`'s rule, for its reason: rebuilding from
                // the vocabulary's order reorders a stored list the contract
                // requires be preserved.
                onChange={(event) =>
                  onChange(
                    event.currentTarget.checked
                      ? [...selected, row.value]
                      : selected.filter((held) => held !== row.value),
                  )
                }
              />
              {/* THE LABEL AND THE WARNING ARE INDEPENDENT. Nesting the
                  warning inside the missing-label fallback tied a fact about the
                  SERVICE to a fact about this BUNDLE: a status the service has
                  stopped publishing but whose English this build still knows
                  rendered as an ordinary "Captured" row, `aria-invalid` and with
                  nothing a sighted reader could see. */}
              <span>
                {ITEM_STATUS_LABELS[row.value as BackgroundItemStatus] ?? (
                  <code dir="ltr">{row.value}</code>
                )}
                {row.unpublished ? (
                  <span className="ml-2 text-destructive">
                    — not a status this engine publishes
                  </span>
                ) : null}
              </span>
            </label>
          </div>
        ))}
      </div>
    </fieldset>
  );
}
